'use client';

import { c as _c } from "react/compiler-runtime";
import { useUser } from '@clerk/nextjs';
import { type DateValue, getLocalTimeZone } from '@internationalized/date';
import { format, parseISO } from 'date-fns';
import { useParams, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useTripId } from '~/hooks/useTripId';
import { api } from '~/libs/api';
import type { TripOverviewLeg } from '~/services/engine-ts';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import { formatTripDateRange, getDateFnsLocale } from '~/utils/dateHelpers/dateHelpers';
import { aggregatePax } from '~/utils/paxHelpers/paxHelper';
import { getTripNights } from '../lib/getTripNights';
import { isTripStartInPast } from '../lib/isTripStartInPast';
import { deriveTripEditability } from '../lib/tripEditability';
import { usePlaceNames } from './usePlaceNames';
import { useTripLiveCheck } from './useTripLiveCheck';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const useTripOverviewSummary = () => {
  const $ = _c(57);
  const tripId = useTripId();
  const locale = useLocale();
  let t0;
  if ($[0] !== tripId) {
    t0 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!tripId;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t1,
      keepPreviousData: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: overviewData
  } = api.trip.getOverview.useQuery(t0, t2);
  let t3;
  if ($[4] !== overviewData?.items) {
    t3 = overviewData?.items.find(_temp);
    $[4] = overviewData?.items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const firstLeg = t3;
  const firstItem = overviewData?.items.find(_temp2);
  const lastItem = overviewData?.items.find(_temp3);
  const originCity = firstLeg?.origin.name ?? "";
  const placeNames = usePlaceNames();
  const originCityLabel = firstLeg && placeNames[firstLeg.origin.id] || originCity;
  const {
    user
  } = useUser();
  let t4;
  if ($[6] !== tripId) {
    t4 = {
      tripId
    };
    $[6] = tripId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !!user && !!tripId;
  let t6;
  if ($[8] !== t5) {
    t6 = {
      enabled: t5,
      ...TRPC_SETTINGS.QUERY_NO_REFETCH
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: isTripSaved
  } = api.account.isTripSaved.useQuery(t4, t6);
  const routeTripId = useParams()?.journeyId;
  const isTemplateDateUnset = useSearchParams()?.get("template") === "1" && routeTripId === tripId;
  let t7;
  if ($[10] !== isTemplateDateUnset || $[11] !== overviewData) {
    t7 = deriveTripEditability(overviewData, isTemplateDateUnset);
    $[10] = isTemplateDateUnset;
    $[11] = overviewData;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    isDateUnset,
    isReadOnly,
    canEditTripSelections
  } = t7;
  const isLiveChecking = useTripLiveCheck(tripId, overviewData?.priceStale === true && !overviewData.requestToBook);
  const confirmedTripId = useTripBookingStore(_temp4);
  let t8;
  if ($[13] !== confirmedTripId || $[14] !== isDateUnset || $[15] !== isTripSaved || $[16] !== overviewData?.previousIds || $[17] !== overviewData?.tripDetailsConfirmedAt || $[18] !== tripId) {
    t8 = !isDateUnset && (!!overviewData?.tripDetailsConfirmedAt || !!isTripSaved || confirmedTripId !== null && (confirmedTripId === tripId || (overviewData?.previousIds ?? []).includes(confirmedTripId)));
    $[13] = confirmedTripId;
    $[14] = isDateUnset;
    $[15] = isTripSaved;
    $[16] = overviewData?.previousIds;
    $[17] = overviewData?.tripDetailsConfirmedAt;
    $[18] = tripId;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const datesConfirmed = t8;
  let t9;
  if ($[20] !== isTemplateDateUnset || $[21] !== overviewData) {
    t9 = isTripStartInPast(overviewData, isTemplateDateUnset);
    $[20] = isTemplateDateUnset;
    $[21] = overviewData;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const isStartDateInPast = t9;
  const fromPrice = overviewData?.requestToBook ? overviewData.fromPrice ?? undefined : undefined;
  let t10;
  if ($[23] !== fromPrice || $[24] !== locale) {
    t10 = fromPrice ? format(parseISO(fromPrice.date), "d MMM yy", {
      locale: getDateFnsLocale(locale)
    }) : undefined;
    $[23] = fromPrice;
    $[24] = locale;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const fromPriceDateLabel = t10;
  const tripDateRange = isDateUnset ? "" : formatTripDateRange(firstItem?.startDate ?? "", lastItem?.endDate ?? "", getDateFnsLocale(locale));
  const dateRange = fromPriceDateLabel ?? tripDateRange;
  let t11;
  if ($[26] !== overviewData?.pax.travellerMixes) {
    t11 = overviewData?.pax.travellerMixes ?? [];
    $[26] = overviewData?.pax.travellerMixes;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = aggregatePax(t11);
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const {
    adultCount,
    childAges
  } = t12;
  const travellerCount = adultCount + childAges.length;
  const rooms = overviewData?.pax.numberOfRooms ?? 1;
  let t13;
  if ($[30] !== overviewData?.items) {
    t13 = overviewData?.items ?? [];
    $[30] = overviewData?.items;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = getTripNights(t13);
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const nights = t14;
  let t15;
  if ($[34] !== overviewData) {
    t15 = date => !!overviewData && (overviewData.unavailableStartDatesForPinnedAccommodation.includes(date.toString()) || overviewData.unavailableDaysOfWeek.includes(WEEKDAYS[date.toDate(getLocalTimeZone()).getDay()]));
    $[34] = overviewData;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const isStartDateUnavailable = t15;
  let t16;
  if ($[36] !== adultCount || $[37] !== canEditTripSelections || $[38] !== childAges || $[39] !== dateRange || $[40] !== datesConfirmed || $[41] !== firstItem || $[42] !== fromPrice || $[43] !== isDateUnset || $[44] !== isLiveChecking || $[45] !== isReadOnly || $[46] !== isStartDateInPast || $[47] !== isStartDateUnavailable || $[48] !== lastItem || $[49] !== nights || $[50] !== originCity || $[51] !== originCityLabel || $[52] !== overviewData || $[53] !== rooms || $[54] !== travellerCount || $[55] !== tripId) {
    t16 = {
      tripId,
      overviewData,
      firstItem,
      lastItem,
      originCity,
      originCityLabel,
      dateRange,
      fromPrice,
      travellerCount,
      adultCount,
      childAges,
      rooms,
      nights,
      isReadOnly,
      isDateUnset,
      canEditTripSelections,
      datesConfirmed,
      isStartDateInPast,
      isLiveChecking,
      isStartDateUnavailable
    };
    $[36] = adultCount;
    $[37] = canEditTripSelections;
    $[38] = childAges;
    $[39] = dateRange;
    $[40] = datesConfirmed;
    $[41] = firstItem;
    $[42] = fromPrice;
    $[43] = isDateUnset;
    $[44] = isLiveChecking;
    $[45] = isReadOnly;
    $[46] = isStartDateInPast;
    $[47] = isStartDateUnavailable;
    $[48] = lastItem;
    $[49] = nights;
    $[50] = originCity;
    $[51] = originCityLabel;
    $[52] = overviewData;
    $[53] = rooms;
    $[54] = travellerCount;
    $[55] = tripId;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
};
function _temp(i) {
  return i.type === "leg";
}
function _temp2(i_0) {
  return i_0.isFirstItem;
}
function _temp3(i_1) {
  return i_1.isLastItem;
}
function _temp4(s) {
  return s.datesConfirmedTripId;
}